const SUPPORTED_SOURCES = new Set(['alfano', 'iracing'])

function round(value, decimals = 3) {
  if (value == null || value === '') return null
  if (!Number.isFinite(Number(value))) return null
  const multiplier = 10 ** decimals
  return Math.round(Number(value) * multiplier) / multiplier
}

function createTelemetryId(source, timestamp = Date.now()) {
  if (!SUPPORTED_SOURCES.has(source)) {
    throw new Error(`Unsupported telemetry source: ${source}`)
  }
  if (!Number.isFinite(Number(timestamp))) {
    throw new Error('Telemetry timestamp must be numeric')
  }
  return `${source}-${Math.trunc(Number(timestamp))}`
}

function lapTimeSeconds(source, lap) {
  const raw = Number(lap && lap.lapTime)
  if (!Number.isFinite(raw) || raw <= 0) return null
  return source === 'alfano' ? round(raw / 1000, 3) : round(raw, 3)
}

function sampleTrace(points, source = 'iracing') {
  if (!Array.isArray(points) || points.length === 0) return []
  const rawStart = Number(points[0].elapsedTime ?? points[0].time)
  const startTime = Number.isFinite(rawStart) ? rawStart : 0
  const timeScale = source === 'alfano' ? 1000 : 1
  const sampledElapsed = points.map(point => Number(point.elapsedTime ?? point.time))
  const wrapIndex = source === 'iracing' ? sampledElapsed.findIndex((value, index) => index > 0 && Number.isFinite(value) && Number.isFinite(sampledElapsed[index - 1]) && value < sampledElapsed[index - 1] - 5) : -1
  const wrappedStart = wrapIndex > 0

  return points.map((point, index) => {
    const rawElapsed = Number(point.elapsedTime ?? point.time)
    return {
    index,
    pct: round(Number.isFinite(Number(point.pct)) ? point.pct : index / Math.max(1, points.length - 1), 4),
    lat: round(point.lat, 7),
    lng: round(point.lng, 7),
    alt: round(point.alt ?? point.altitude, 2),
    speed: round(point.speed, 3),
    rpm: round(point.rpm, 0),
    gear: round(point.gear, 0),
    throttle: round(point.throttle, 3),
    brake: round(point.brake, 3),
    steering: round(point.steering, 4),
    latAccel: round(point.latAccel, 3),
    longAccel: round(point.longAccel, 3),
    yawRate: round(point.yawRate, 4),
    engineTemp: round(point.engineTemp, 1),
    fuelLevel: round(point.fuelLevel, 2),
    elapsedSeconds: Number.isFinite(rawElapsed) ? round((wrappedStart ? (index < wrapIndex ? 0 : rawElapsed) : rawElapsed - startTime) / timeScale, 4) : null,
    dist: round(point.dist, 2),
    }
  })
}

function sectorDurations(lapSeconds, points) {
  if (!lapSeconds || !Array.isArray(points) || points.length < 2) return [null, null, null]
  const progress = points.map((point, index) => {
    const pct = Number(point.pct)
    return Number.isFinite(pct) && pct >= 0 && pct <= 1
      ? pct
      : index / (points.length - 1)
  })
  const first = progress.findIndex(value => value >= 1 / 3)
  const second = progress.findIndex(value => value >= 2 / 3)
  const firstRatio = first > 0 ? first / (points.length - 1) : 1 / 3
  const secondRatio = second > first ? second / (points.length - 1) : 2 / 3
  return [
    round(lapSeconds * firstRatio, 3),
    round(lapSeconds * (secondRatio - firstRatio), 3),
    round(lapSeconds * (1 - secondRatio), 3),
  ]
}

function standardDeviation(values) {
  if (!values.length) return 0
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length
  const variance = values.reduce((sum, value) => sum + ((value - mean) ** 2), 0) / values.length
  return Math.sqrt(variance)
}

function maximum(values) {
  const numeric = values.filter(Number.isFinite)
  return numeric.length ? Math.max(...numeric) : null
}

function interpolateTracePoint(points, pct) {
  if (!points.length) return null
  const foundIndex = points.findIndex(point => point.pct >= pct)
  const upperIndex = foundIndex === -1 ? points.length - 1 : foundIndex
  const upper = points[upperIndex]
  const lower = points[Math.max(0, upperIndex - 1)]
  if (!lower || lower === upper || upper.pct === lower.pct) return upper
  const ratio = (pct - lower.pct) / (upper.pct - lower.pct)
  const interpolate = key => Number.isFinite(lower[key]) && Number.isFinite(upper[key])
    ? lower[key] + (upper[key] - lower[key]) * ratio
    : null
  return { ...upper, pct, elapsedSeconds: interpolate('elapsedSeconds'), speed: interpolate('speed') }
}

function buildComparison(referenceTrace, selectedTrace, corners = []) {
  const deltaTrace = selectedTrace.points.map(point => {
    const reference = interpolateTracePoint(referenceTrace.points, point.pct)
    return {
      distancePct: point.pct,
      deltaSeconds: Number.isFinite(point.elapsedSeconds) && Number.isFinite(reference?.elapsedSeconds)
        ? round(point.elapsedSeconds - reference.elapsedSeconds, 4)
        : null,
      selectedSpeed: point.speed,
      referenceSpeed: reference?.speed ?? null,
    }
  })
  const lineGaps = selectedTrace.points.map(point => {
    const reference = interpolateTracePoint(referenceTrace.points, point.pct)
    return reference ? haversineMeters(point, reference) : null
  }).filter(Number.isFinite)
  return {
    referenceLapNumber: referenceTrace.lapNumber,
    selectedLapNumber: selectedTrace.lapNumber,
    deltaTrace,
    corners: corners.map(corner => {
      const reference = interpolateTracePoint(referenceTrace.points, corner.apexPct)
      const selected = interpolateTracePoint(selectedTrace.points, corner.apexPct)
      return {
        number: corner.number,
        apexPct: corner.apexPct,
        minimumSpeedDelta: round((selected?.speed ?? 0) - (reference?.speed ?? 0), 3),
        brakeDelta: Number.isFinite(selected?.brake) && Number.isFinite(reference?.brake) ? round(selected.brake - reference.brake, 3) : null,
        throttleDelta: Number.isFinite(selected?.throttle) && Number.isFinite(reference?.throttle) ? round(selected.throttle - reference.throttle, 3) : null,
      }
    }),
    racingLine: {
      averageGapMeters: lineGaps.length ? round(lineGaps.reduce((sum, value) => sum + value, 0) / lineGaps.length, 2) : null,
      maximumGapMeters: lineGaps.length ? round(Math.max(...lineGaps), 2) : null,
    },
  }
}

function contiguousRanges(points, predicate) {
  const ranges = []
  let start = null
  points.forEach((point, index) => {
    const active = predicate(point)
    if (active && start === null) start = index
    if ((!active || index === points.length - 1) && start !== null) {
      const end = active && index === points.length - 1 ? index : index - 1
      if (end - start >= 1) ranges.push([start, end])
      start = null
    }
  })
  return ranges
}

function deriveCorners(points) {
  const speeds = points.map(point => point.speed).filter(Number.isFinite).sort((a, b) => a - b)
  const threshold = speeds.length ? speeds[Math.floor(speeds.length * 0.55)] : null
  if (!Number.isFinite(threshold)) return []
  const candidates = []
  for (let index = 2; index < points.length - 2; index += 1) {
    const speed = points[index].speed
    if (Number.isFinite(speed) && speed <= threshold && speed <= points[index - 1].speed && speed < points[index + 1].speed) {
      if (!candidates.length || points[index].pct - points[candidates[candidates.length - 1]].pct >= 0.035) candidates.push(index)
    }
  }
  return candidates.slice(0, 24).map((apexIndex, index) => {
    const entry = points[Math.max(0, apexIndex - 3)]
    const apex = points[apexIndex]
    const exit = points[Math.min(points.length - 1, apexIndex + 3)]
    const gears = points.slice(Math.max(0, apexIndex - 3), apexIndex + 4).map(point => point.gear).filter(Number.isFinite)
    return {
      number: index + 1,
      entryPct: entry.pct,
      apexPct: apex.pct,
      exitPct: exit.pct,
      minimumSpeed: apex.speed,
      entrySpeed: entry.speed,
      exitSpeed: exit.speed,
      brakePeak: maximum(points.slice(Math.max(0, apexIndex - 5), apexIndex + 2).map(point => point.brake)),
      minimumGear: gears.length ? Math.min(...gears) : null,
    }
  })
}

function haversineMeters(a, b) {
  if (![a.lat, a.lng, b.lat, b.lng].every(Number.isFinite)) return 0
  const radians = degrees => degrees * Math.PI / 180
  const dLat = radians(b.lat - a.lat)
  const dLng = radians(b.lng - a.lng)
  const sinLat = Math.sin(dLat / 2)
  const sinLng = Math.sin(dLng / 2)
  const value = sinLat ** 2 + Math.cos(radians(a.lat)) * Math.cos(radians(b.lat)) * sinLng ** 2
  return 6371000 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value))
}

function deriveReferenceAnalysis(referenceTrace) {
  const points = referenceTrace.points
  const brakingZones = contiguousRanges(points, point => Number(point.brake) > 0.1).map(([start, end], index) => ({
    number: index + 1,
    startPct: points[start].pct,
    endPct: points[end].pct,
    peakBrake: maximum(points.slice(start, end + 1).map(point => point.brake)),
  }))
  const gearShifts = points.slice(1).reduce((events, point, index) => {
    const previous = points[index]
    if (Number.isFinite(point.gear) && Number.isFinite(previous.gear) && point.gear !== previous.gear) {
      events.push({ distancePct: point.pct, fromGear: previous.gear, toGear: point.gear })
    }
    return events
  }, [])
  const rpmBands = new Map()
  const gears = new Map()
  points.forEach(point => {
    if (Number.isFinite(point.rpm)) {
      const minimum = Math.floor(point.rpm / 1000) * 1000
      rpmBands.set(minimum, (rpmBands.get(minimum) || 0) + 1)
    }
    if (Number.isFinite(point.gear)) gears.set(point.gear, (gears.get(point.gear) || 0) + 1)
  })
  const distanceMeters = points.slice(1).reduce((sum, point, index) => sum + haversineMeters(points[index], point), 0)
  return {
    corners: deriveCorners(points),
    events: { brakingZones, gearShifts },
    distributions: {
      rpm: [...rpmBands.entries()].sort((a, b) => a[0] - b[0]).map(([minimum, sampleCount]) => ({ minimum, maximum: minimum + 999, sampleCount })),
      gear: [...gears.entries()].sort((a, b) => a[0] - b[0]).map(([gear, sampleCount]) => ({ gear, sampleCount })),
    },
    racingLine: { lapNumber: referenceTrace.lapNumber, pointCount: points.length, distanceMeters: round(distanceMeters, 1) },
  }
}

function buildTelemetryReport({ source, allLaps, selectedLapNumber = null, trackInfo = {}, file = {} } = {}) {
  if (!SUPPORTED_SOURCES.has(source)) {
    throw new Error(`Unsupported telemetry source: ${source}`)
  }
  if (!Array.isArray(allLaps) || allLaps.length === 0) {
    throw new Error('At least one telemetry lap is required')
  }

  const normalizedLaps = allLaps.map(lap => {
    const seconds = lapTimeSeconds(source, lap)
    const trace = sampleTrace(lap.points, source)
    const sectors = sectorDurations(seconds, lap.points)
    const measuredPoints = Array.isArray(lap.points) ? lap.points : []
    const speeds = measuredPoints.map(point => round(point.speed, 3)).filter(Number.isFinite)
    const rpms = measuredPoints.map(point => round(point.rpm, 0)).filter(Number.isFinite)
    return {
      lapNumber: Number(lap.lapNumber),
      lapTimeSeconds: seconds,
      formattedTime: lap.formattedTime || null,
      isClean: lap.isClean !== false,
      hasIncident: Boolean(lap.hasIncident),
      sectors,
      pointCount: Array.isArray(lap.points) ? lap.points.length : 0,
      maxSpeed: speeds.length ? round(Math.max(...speeds), 3) : null,
      averageSpeed: speeds.length ? round(speeds.reduce((sum, value) => sum + value, 0) / speeds.length, 3) : null,
      maxRpm: rpms.length ? Math.max(...rpms) : null,
    }
  }).filter(lap => lap.lapTimeSeconds)

  if (!normalizedLaps.length) throw new Error('No timed laps were found')

  const cleanLaps = normalizedLaps.filter(lap => lap.isClean)
  const comparableLaps = cleanLaps.length ? cleanLaps : normalizedLaps
  const bestLap = comparableLaps.reduce((best, lap) => lap.lapTimeSeconds < best.lapTimeSeconds ? lap : best)
  const selected = normalizedLaps.find(lap => lap.lapNumber === Number(selectedLapNumber)) || bestLap
  const selectedRaw = allLaps.find(lap => Number(lap.lapNumber) === selected.lapNumber) || allLaps[0]
  const traceLaps = normalizedLaps
    .slice()
    .sort((a, b) => a.lapTimeSeconds - b.lapTimeSeconds)
  if (!traceLaps.some(lap => lap.lapNumber === selected.lapNumber)) traceLaps.push(selected)
  const traces = traceLaps.map(lap => {
    const raw = allLaps.find(item => Number(item.lapNumber) === lap.lapNumber)
    return { lapNumber: lap.lapNumber, isClean: lap.isClean, points: sampleTrace(raw && raw.points, source) }
  })
  const referenceTrace = traces.find(trace => trace.lapNumber === bestLap.lapNumber)
  if (referenceTrace?.points.some(point => point.elapsedSeconds !== null)) {
    traces.forEach(trace => {
      trace.points = trace.points.map(point => {
        const referencePoint = interpolateTracePoint(referenceTrace.points, point.pct)
        return {
          ...point,
          delta: point.elapsedSeconds === null || referencePoint?.elapsedSeconds === null
            ? null
            : round(point.elapsedSeconds - referencePoint.elapsedSeconds, 4),
        }
      })
    })
  }

  const sectorBests = [0, 1, 2].map(index => Math.min(...comparableLaps.map(lap => lap.sectors[index]).filter(Number.isFinite)))
  const theoreticalBestSeconds = Math.min(
    bestLap.lapTimeSeconds,
    round(sectorBests.reduce((sum, value) => sum + value, 0), 3)
  )
  const cleanTimes = comparableLaps.map(lap => lap.lapTimeSeconds)
  const average = cleanTimes.reduce((sum, value) => sum + value, 0) / cleanTimes.length
  const coefficient = average ? standardDeviation(cleanTimes) / average : 0
  const consistencyScore = Math.max(0, Math.min(100, Math.round(100 - coefficient * 1500)))
  const selectedTrace = traces.find(trace => trace.lapNumber === selected.lapNumber)?.points || sampleTrace(selectedRaw && selectedRaw.points, source)
  const channels = ['speed', 'rpm', 'gear', 'throttle', 'brake', 'steering', 'latAccel', 'longAccel', 'yawRate', 'engineTemp', 'fuelLevel', 'alt', 'delta'].filter(channel =>
    selectedTrace.some(point => point[channel] !== null)
  )
  const referenceAnalysis = deriveReferenceAnalysis(referenceTrace)
  const referenceRaw = allLaps.find(lap => Number(lap.lapNumber) === bestLap.lapNumber)
  const measuredGpsPointCount = Array.isArray(referenceRaw?.points)
    ? referenceRaw.points.filter(point => Number.isFinite(Number(point.lat)) && Number.isFinite(Number(point.lng))).length
    : referenceAnalysis.racingLine.pointCount
  const comparisons = buildComparison(referenceTrace, traces.find(trace => trace.lapNumber === selected.lapNumber) || referenceTrace, referenceAnalysis.corners)
  const sectorHeatmap = normalizedLaps.map(lap => ({
    lapNumber: lap.lapNumber,
    sectors: lap.sectors.map((seconds, index) => ({
      number: index + 1,
      seconds,
      deltaToBest: round(seconds - sectorBests[index], 3),
    })),
  }))

  return {
    schemaVersion: 1,
    reportType: 'deterministic-session-analysis',
    source,
    generatedAt: new Date().toISOString(),
    aiCoachingStatus: 'excluded',
    track: {
      name: trackInfo.name || null,
      configuration: trackInfo.configuration || null,
      lengthMeters: round(trackInfo.length, 1),
    },
    vehicle: {
      name: trackInfo.carName || null,
      id: trackInfo.carId || null,
    },
    sourceFile: {
      name: file.name || null,
      size: Number.isFinite(Number(file.size)) ? Number(file.size) : null,
      contentType: file.contentType || null,
      retention: file.retention || 'private_original',
    },
    summary: {
      totalLapCount: normalizedLaps.length,
      cleanLapCount: cleanLaps.length,
      bestLapNumber: bestLap.lapNumber,
      bestLapSeconds: bestLap.lapTimeSeconds,
      selectedLapNumber: selected.lapNumber,
      theoreticalBestSeconds,
      timeAvailableSeconds: Math.max(0, round(bestLap.lapTimeSeconds - theoreticalBestSeconds, 3)),
      averageLapSeconds: round(average, 3),
      consistencyScore,
      maxSpeed: maximum(normalizedLaps.map(lap => lap.maxSpeed)),
      maxRpm: maximum(normalizedLaps.map(lap => lap.maxRpm)),
    },
    sectors: sectorBests.map((seconds, index) => ({ number: index + 1, bestSeconds: round(seconds, 3) })),
    sectorHeatmap,
    laps: normalizedLaps,
    channels,
    units: {
      speed: source === 'alfano' ? 'km/h' : 'm/s',
      rpm: 'rpm',
      throttle: 'ratio',
      brake: 'ratio',
    },
    traces,
    comparisons,
    ...referenceAnalysis,
    racingLine: { ...referenceAnalysis.racingLine, pointCount: measuredGpsPointCount },
  }
}

module.exports = {
  buildTelemetryReport,
  createTelemetryId,
  sampleTrace,
}
